import { DocumentSlot, api } from './api'

/**
 * Hears about this person's saves made somewhere else — another tab, their
 * phone — from the Mercure hub the server runs (see the backend's LiveUpdates).
 *
 * The news is only "slot X is now at version N"; whoever's listening decides
 * whether to fetch it. One connection per tab, shared by every listener, and
 * closed when the last one goes. Where the browser has no EventSource (tests,
 * very old browsers) nothing happens and the app works as it did before: a
 * stale tab finds out when its next save meets the version check.
 */

export interface RemoteSave {
  /** `'*'` after reconnecting: anything may have changed while the line was down. */
  slot: DocumentSlot | '*'
  version: number
}

type Listener = (save: RemoteSave) => void

const RETRY_MIN_MS = 2_000
const RETRY_MAX_MS = 60_000

let connection: {
  userId: string
  listeners: Set<Listener>
  source: EventSource | null
  retry: ReturnType<typeof setTimeout> | null
  backoff: number
  everOpened: boolean
  closed: boolean
} | null = null

export function onRemoteSave(userId: string, listener: Listener): () => void {
  if (typeof EventSource === 'undefined') return () => {}

  if (connection && connection.userId !== userId) close()
  if (!connection) {
    connection = { userId, listeners: new Set(), source: null, retry: null, backoff: RETRY_MIN_MS, everOpened: false, closed: false }
    void connect(connection)
  }
  const mine = connection
  mine.listeners.add(listener)

  return () => {
    mine.listeners.delete(listener)
    if (mine.listeners.size === 0 && connection === mine) close()
  }
}

async function connect(c: NonNullable<typeof connection>) {
  let live: Awaited<ReturnType<typeof api.live>>
  try {
    // Also renews the cookie the hub checks, which is why a closed stream
    // comes back through here rather than reconnecting on its own.
    live = await api.live()
  } catch {
    scheduleRetry(c)
    return
  }
  if (c.closed || !live.enabled || !live.hub || !live.topic) return

  const source = new EventSource(`${live.hub}?match=${encodeURIComponent(live.topic)}`)
  c.source = source

  source.onopen = () => {
    c.backoff = RETRY_MIN_MS
    // Saves made while this tab was disconnected weren't heard, and the hub
    // keeps no history, so after a reconnect every listener checks for itself.
    if (c.everOpened) for (const l of c.listeners) l({ slot: '*', version: Number.POSITIVE_INFINITY })
    c.everOpened = true
  }
  source.onmessage = (e) => {
    let save: RemoteSave
    try {
      const data = JSON.parse(e.data) as { slot?: unknown; version?: unknown }
      if ((data.slot !== 'main' && data.slot !== 'sandbox') || typeof data.version !== 'number') return
      save = { slot: data.slot, version: data.version }
    } catch {
      return
    }
    for (const l of c.listeners) l(save)
  }
  source.onerror = () => {
    // EventSource retries a dropped connection by itself. It gives up — CLOSED
    // — when the hub refuses it, which is what an expired cookie looks like.
    if (source.readyState === EventSource.CLOSED) {
      source.close()
      c.source = null
      scheduleRetry(c)
    }
  }
}

function scheduleRetry(c: NonNullable<typeof connection>) {
  if (c.closed || c.retry) return
  const wait = c.backoff
  c.backoff = Math.min(wait * 2, RETRY_MAX_MS)
  c.retry = setTimeout(() => {
    c.retry = null
    void connect(c)
  }, wait)
}

function close() {
  if (!connection) return
  connection.closed = true
  connection.source?.close()
  if (connection.retry) clearTimeout(connection.retry)
  connection = null
}
